<template>
  <div class="select-date-modal">
    <div class="select-date-modal__content">
      <div v-if="showCalendar" class="select-date-modal__calendar-wrapper">
        <q-date
          v-model="selectedDate"
          mask="DD.MM.YYYY"
          :locale="dateLocaleRu"
          minimal
          class="select-date-modal__calendar tw-rounded-borders"
        />
      </div>
      <div v-if="certificates.length" class="select-date-modal__table-section">
        <p class="select-date-modal__warning">
          Загрузите сертификаты на используемые материалы.
          <br>
          Без сертификатов не будет создан АСР
        </p>
        <p class="select-date-modal__hint">
          Для загрузки сертификата нажмите на материал или пройдите в
          <q-btn
            flat
            dense
            no-caps
            class="select-date-modal__inline-btn"
            label="Сертификаты проекта"
            :disable="!firstCertificateUrl"
            @click="openProjectCertificates"
          />
        </p>
        <div class="select-date-modal__table-wrapper">
          <q-table
            :rows="certificates"
            :columns="certificateColumns"
            row-key="material_id"
            flat
            dense
            hide-pagination
            :pagination="{ rowsPerPage: 0 }"
            class="select-date-modal__table"
          >
          <template v-slot:body-cell-material_name="props">
            <q-td :props="props">
              <a
                href="#"
                @click.prevent="openMaterialEditor(props.row)"
                class="select-date-modal__link"
              >
                {{ props.row.material_name }}
              </a>
            </q-td>
          </template>
          </q-table>
        </div>
        <p class="select-date-modal__note">
          *Вы все еще можете вызвать ОКК на группу работ даже без сертификатов
        </p>
      </div>
    </div>
    <CustomModalFooter
      :btn-loading="loading"
      :submit-btn-text="submitBtnText"
      :cancel-btn-text="cancelBtnText"
      :on-submit="onSubmit"
      :onCancel="closeModal"
    />
  </div>
</template>

<script>
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import { useAppStore } from "@/services/store/app";
import { dateLocaleRu } from "@/utils";

function getTodayFormatted() {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
}

export default {
  name: "SelectDateModal",
  components: { CustomModalFooter },
  // Формат даты: DD.MM.YYYY (принимается в defaultDate и передаётся в onConfirm)
  props: {
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    const defaultDate = this.data?.defaultDate ?? getTodayFormatted();
    return {
      loading: false,
      selectedDate: defaultDate,
      dateLocaleRu,
      certificateColumns: [
        { name: "material_id", label: "ID", field: "material_id", align: "left" },
        { name: "material_name", label: "Материал", field: "material_name", align: "left" },
      ],
    };
  },
  computed: {
    certificates() {
      const arr = this.data?.certificates ?? [];
      return Array.isArray(arr) ? arr : [];
    },
    projectId() {
      return this.data?.projectId || null;
    },
    firstCertificateUrl() {
      return this.certificates?.[0]?.certificate_url || "";
    },
    showCalendar() {
      return this.data?.showCalendar !== false;
    },
    submitBtnText() {
      return this.data?.submitBtnText ?? (this.showCalendar ? "Подтвердить" : "Продолжить");
    },
    cancelBtnText() {
      return this.data?.cancelBtnText ?? "Отмена";
    },
  },
  methods: {
    openProjectCertificates() {
      if (!this.firstCertificateUrl) return;
      window.open(this.firstCertificateUrl, "_blank", "noopener,noreferrer");
    },
    openMaterialEditor(material) {
      if (!this.projectId || !material?.material_id) return;

      this.showSecondaryModal("AddMaterialCertificate", {
        title: "Редактировать сертификат",
        projectId: this.projectId,
        material,
      });
    },
    async onSubmit() {
      this.loading = true;
      try {
        const onConfirm = this.data?.onConfirm;
        if (onConfirm) {
          const date = this.showCalendar ? this.selectedDate : null;
          await onConfirm(date);
        }
      } finally {
        this.loading = false;
      }
    },
  },
  setup() {
    const appStore = useAppStore();
    const { closeModal, showSecondaryModal } = appStore;
    return { closeModal, showSecondaryModal };
  },
};
</script>

<style scoped lang="scss">
.select-date-modal {
  min-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__table-section {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__warning {
    margin: 0;
    color: #d32f2f;
    font-size: 14px;
  }

  &__note {
    margin: 0;
    font-size: 14px;
    color: #666;
  }

  &__hint {
    margin: 0;
    font-size: 13px;
    color: #555;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__content {
    display: flex;
    flex-direction: row;
    gap: 20px;
    align-items: flex-start;
  }

  &__calendar-wrapper {
    flex-shrink: 0;
  }

  &__calendar {
    margin-bottom: 0;
  }

  &__table-wrapper {
    max-height: 300px;
    overflow: auto;
  }

  &__table {
    font-size: 13px;
  }

  &__link {
    color: #1976d2;
    text-decoration: underline;
  }

  &__inline-btn {
    color: #1976d2;
    text-decoration: underline;
    padding: 0;
    min-height: 20px;
  }
}
</style>
